import _ from 'underscore';
import React, { useContext, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { Text } from '@fluentui/react';
import { ValidationGroup, Input } from '@bingads-webui-react/primitive-validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { WebsiteInput } from './website-input';
import { websiteAndBusinessSchema } from './validation-schema';
import { businessNameValidationErrors } from './consts';
import { getWebsiteAndBusinessStyles as getStyles, websiteAndBusinessStyles as styles } from './styles';
import type { ValidationResult, WebsiteAndBusinessProps } from '../types';

export const WebsiteAndBusiness = (props: WebsiteAndBusinessProps) => {
  const {
    website,
    setWebsite,
    businessName,
    setBusinessName,
    setIsValid,
    alwaysShowError,
  } = props;
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const classes = useDefaultStyles(getStyles);

  const onValidate = useCallback((e: ValidationResult) => {
    setIsValid(e.isValid);
  }, [setIsValid]);

  const onChangeBusinessName = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessName(value);
  }, [setBusinessName]);
  const errorProcessor = useCallback((errors: Record<string, string>) => {
    const errorStrings = _.map(
      errors,
      (oldError: string, key: keyof typeof businessNameValidationErrors) => (
        _.has(businessNameValidationErrors, key) ?
          i18n.getString(businessNameValidationErrors[key]) : oldError)
    );
    return _.isEmpty(errorStrings) ? [] : [_.last(errorStrings)];
  }, [i18n]);

  return (
    <div className={classNames('website-and-business', classes.container)}>
      <ValidationGroup
        validationProps={{
          schema: websiteAndBusinessSchema,
          i18n: legacyI18n,
          onValidate,
        }}
      >
        <div className="business-name">
          <Text
            variant="medium"
            block
            styles={styles.title}
          >
            {i18n.getString(_TL_('What\'s the name of your business?'))}
          </Text>
          <Input
            id="business-name-input"
            aria-labelledby="businessName"
            data-clarity-unmask="true"
            className={classNames(['business-name', classes.businessName])}
            name="business"
            value={businessName}
            validationProps={{
              field: 'business',
              errorProcessor,
              alwaysShowError,
            }}
            onChange={onChangeBusinessName}
            placeholder={i18n.getString(_TL_('Your business name'))}
          />
        </div>
        <Text
          variant="medium"
          block
          styles={styles.titleWithMargin}
        >
          {i18n.getString(_TL_('What\'s your business website?'))}
        </Text>
        <WebsiteInput
          website={website}
          setWebsite={setWebsite}
          alwaysShowError={alwaysShowError}
          style={styles.website}
        />
      </ValidationGroup>
    </div >);
};

WebsiteAndBusiness.propTypes = {
  website: PropTypes.string,
  setWebsite: PropTypes.func.isRequired,
  businessName: PropTypes.string,
  setBusinessName: PropTypes.func.isRequired,
  setIsValid: PropTypes.func,
  alwaysShowError: PropTypes.bool,
};

WebsiteAndBusiness.defaultProps = {
  website: undefined,
  businessName: undefined,
  setIsValid: _.noop,
  alwaysShowError: false,
};
